import { useEffect, SetStateAction, Dispatch, ChangeEvent } from 'react';
import { useSelector } from 'react-redux';
import { useTranslation } from 'react-i18next';

import './index.css';
import { ContributionTable } from './components';
import { EmployerContribution } from './EmployerContribution';
import { ContributionAmountResponseDto } from 'enrollment/queries/useContributionAmountQuery';
import { ContributionLumpSum } from './ContributionLumpSum';
import { formatDateString } from 'enrollment/features/EnrollmentDashboard/components/EnrollmentDashboardPage/utils';
import { FormElement } from 'enrollment/components/Shared/FormElement/';
import { getEEContributionDisplay, valueWithinBounds } from './utils';
import { selectPerPayPeriodText } from 'enrollment/services';
import { convertCurrency } from 'enrollment/utils';

interface ContributionElectionProps {
  contributionData: ContributionAmountResponseDto;
  eeContributionPPP: number;
  eeContributionAnnual: number;
  isPerPayPeriod: boolean;
  enrollmentMethod: string;
  planId: number;
  setEeContributionPPP?: Dispatch<SetStateAction<number>>;
  setEeContributionAnnual?: Dispatch<SetStateAction<number>>;
  contributionEndDate?: string;
  setContributionEndDate?: Dispatch<SetStateAction<string>>;
  eeLumpSumAmount: number;
  setEeLumpSumAmount?: Dispatch<SetStateAction<number>>;
  isLoading?: boolean;
}

export const ContributionElection = ({
  contributionData,
  eeContributionPPP,
  eeContributionAnnual,
  isPerPayPeriod,
  enrollmentMethod,
  setEeContributionPPP,
  setEeContributionAnnual,
  contributionEndDate,
  setContributionEndDate,
  eeLumpSumAmount,
  setEeLumpSumAmount,
  isLoading,
  planId,
}: ContributionElectionProps) => {
  const { t } = useTranslation('pages.financial');

  const perPayText = useSelector(selectPerPayPeriodText);

  useEffect(() => {
    if (!isLoading) {
      const input = getEEInputElem();
      if (input) {
        const min = contributionData.data.calculated_subscriber_min || 0;
        const max = contributionData.data.calculated_subscriber_max || 0;
        if (isPerPayPeriod) {
          input.value = String(
            Number(valueWithinBounds(eeContributionPPP, min, max)).toFixed(2)
          );
        } else {
          input.value = String(
            Number(valueWithinBounds(eeContributionAnnual, min, max)).toFixed(2)
          );
        }
      }

      const contributionEndDateInput = document.getElementById(
        `contribution-end-date-${planId}`
      ) as HTMLInputElement;
      if (
        contributionEndDateInput &&
        contributionData?.data.hsa_contribution_end_date
      ) {
        const date = contributionData?.data.hsa_contribution_end_date;
        contributionEndDateInput.value = date
          ? formatDateString(String(date))
          : '';
      }

      const helpTextContainer = document.getElementById(
        `personal-contribution-${planId}`
      )?.parentElement;
      if (helpTextContainer) {
        const helpText =
          helpTextContainer.getElementsByClassName('helper-text')[0];
        helpText.textContent = `${t('min')} ${convertCurrency(
          String(contributionData.data.calculated_subscriber_min || 0)
        )} | ${t('max')} ${convertCurrency(String(contributionData.data.calculated_subscriber_max || 0))}`;
      }

      const continueBtn = document.getElementById(
        `submit-contribution-${planId}`
      ) as HTMLElement;
      if (
        eeContributionAnnual >= contributionData.data.subscriber_min_amount &&
        eeContributionAnnual <= contributionData.data.subscriber_max_amount
      ) {
        continueBtn.removeAttribute('disabled');
      } else {
        continueBtn.setAttribute('disabled', 'true');
      }
    }
  }, [
    eeContributionPPP,
    eeContributionAnnual,
    eeLumpSumAmount,
    isPerPayPeriod,
    contributionData,
    isLoading,
    t,
    setEeLumpSumAmount,
  ]);

  const setEeContribution = (value: number) => {
    if (setEeContributionPPP && setEeContributionAnnual) {
      if (isPerPayPeriod) {
        setEeContributionPPP(value);
        setEeContributionAnnual(
          getEEContributionDisplay(value, contributionData, enrollmentMethod)
        );
      } else {
        // If we round here we potentially cause issues for certain configurations where the rounded ppp value
        // is greater than the calculated max returned from the API. Truncating it to 2 decimal places
        // allows us to keep the value within the bounds of the max and min.
        const contributionPpp = Math.trunc((value / contributionData.data.remaining_paydays) * 100) / 100;
        setEeContributionPPP(contributionPpp);
        setEeContributionAnnual(value);
      }
    }
  };

  const displayEELumpSum =
    Boolean(contributionData?.data.subscriber_include_lump_sum_contribution);
  const displayERLumpSum =
    Boolean(contributionData?.data.org_include_lump_sum_contribution);

  const erLumpSumAmount =
    Number(contributionData.data.er_lump_sum_contribution_amount) || 0;
  const erAnnualAmount = Number(contributionData?.data.org_fsa_amount) || 0;
  const totalErAnnual = erAnnualAmount + erLumpSumAmount;

  const totalEeAnnual = eeContributionAnnual + eeLumpSumAmount;

  const totalCombinedContribution =
    totalErAnnual + eeContributionAnnual + eeLumpSumAmount;

  const setMaxValue = () => {
    const input = getEEInputElem();
    const calculatedSubscriberMax =
      contributionData.data.calculated_subscriber_max.toFixed(2);
    input.value = String(calculatedSubscriberMax);
    setEeContribution(Number(calculatedSubscriberMax));
  };

  const handleContributionChange = (
    event: React.ChangeEvent<HTMLInputElement>
  ) => {
    const input = getEEInputElem();
    const minValOrUserInput = Math.max(
      Number(event.target.value),
      contributionData.data.calculated_subscriber_min
    );
    const newValue = Math.min(
      minValOrUserInput,
      contributionData.data.calculated_subscriber_max
    );
    input.value = String(newValue);
    setEeContribution(newValue);
  };

  const getEEInputElem = () => {
    return document.getElementById(
      `personal-contribution-${planId}`
    ) as HTMLInputElement;
  };

  const handleEndDateChange = (
    event: ChangeEvent<HTMLInputElement | HTMLSelectElement>
  ) => {
    if (setContributionEndDate) {
      setContributionEndDate(event.target.value);
    }
  };

  const displayValue = isPerPayPeriod
    ? eeContributionPPP
    : eeContributionAnnual;

  const validOrgFsaAmount =
    Boolean(contributionData.data.org_fsa_amount) &&
    Number(contributionData.data.org_fsa_amount) !== 0;

  const validOrgPremium =
    Boolean(contributionData.data.org_premium) &&
    Number(contributionData.data.org_premium) !== 0;

  return (
    <div>
      <div className="row mt-2">
        <div className="col-md-5">
          <ps-numeric-field
            data-testid="contribution-input"
            input-id={`personal-contribution-${planId}`}
            label={t('personal')}
            value={Number(displayValue).toFixed(2)}
            icon-left
            required
            icon="currency-dollar"
            onBlur={handleContributionChange}
            help-text={`${t('min')} ${convertCurrency(
              String(contributionData.data.calculated_subscriber_min || 0)
            )} | ${t('max')} ${convertCurrency(
              String(contributionData.data.calculated_subscriber_max || 0)
            )}`}
          ></ps-numeric-field>
        </div>
        <div id="maximize-contribution" className="col-md-4">
          <ps-button
            id={`maximize-${planId}`}
            level="secondary"
            label={t('maximize')}
            onClick={setMaxValue}
          ></ps-button>
        </div>
      </div>
      {!displayERLumpSum && validOrgFsaAmount && (
        <EmployerContribution
          value={Number(contributionData.data.org_fsa_amount) || 0}
          contributionData={contributionData}
        />
      )}
      {contributionData?.data.enable_contribution_end_date && (
        <div className="row mt-2">
          <div className="col-md-5">
            <FormElement
              formField="date"
              inputId={`contribution-end-date-${planId}`}
              label={t('contributionEndDate')}
              name={`contribution-end-date-${planId}`}
              value={contributionEndDate}
              onChange={(event) => handleEndDateChange(event)}
            />
          </div>
        </div>
      )}
      {(displayEELumpSum || displayERLumpSum) && (
        <ContributionLumpSum
          contributionData={contributionData}
          eeLumpSumAmount={eeLumpSumAmount}
          totalEeAnnual={totalEeAnnual}
          totalErAnnual={totalErAnnual}
          displayEELumpSum={displayEELumpSum}
          displayERLumpSum={displayERLumpSum}
          setEeLumpSumAmount={setEeLumpSumAmount}
          planId={planId}
        />
      )}
      <div className="row my-4">
        <ContributionTable
          title={t(`${enrollmentMethod}_totalContribution`)}
          value={Number(totalCombinedContribution).toFixed(2)}
        ></ContributionTable>
        {validOrgPremium && (
          <ContributionTable
            title={t('perPayPeriodContribution', { pppText: perPayText })}
            value={Number(contributionData.data.org_premium).toFixed(2)}
          ></ContributionTable>
        )}
      </div>
      <div className="border-top py-4">
        <div>
          <p className="body2 semiBold">
            {t('personal')}: {convertCurrency(String(totalEeAnnual))}
          </p>
          <p className="helper-text">
            {convertCurrency(String(totalEeAnnual))}{' '}
            {t(`${enrollmentMethod}_contribution`)} =
            {convertCurrency(String(eeContributionPPP))}{' '}
            {t('PPPContribution', { pppText: perPayText })}{' '}
            {eeLumpSumAmount > 0 &&
              `+ ${convertCurrency(String(eeLumpSumAmount))} ${t('lumpSum.lumpSumContribution')}`}
          </p>
        </div>
        {totalErAnnual > 0 && (
          <div>
            <p className="body2 semiBold">
              {t('employer')}: {convertCurrency(String(totalErAnnual))}
            </p>
            <p className="helper-text">
              {convertCurrency(String(totalErAnnual))}{' '}
              {t(`${enrollmentMethod}_contribution`)} =
              {convertCurrency(String(contributionData.data.org_premium))}{' '}
              {t('PPPContribution', { pppText: perPayText })}{' '}
              {erLumpSumAmount > 0 &&
                `+ ${convertCurrency(String(erLumpSumAmount))} ${t('lumpSum.lumpSumContribution')}`}
            </p>
          </div>
        )}
      </div>
    </div>
  );
};
